import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import Navbar from "../../../components/navbar";
import Footer from "../../../components/footer";
import Switcher from "../../../components/switcher";
import ScrollToTop from "../../../components/scroll-to-top";

import {
  FiHeart,
  FiEye,
  FiBookmark,
  FiChevronLeft,
  FiChevronRight,
} from "../../../assets/icons/vander";
import { useGetProductsQuery } from "../../../redux/services/products";
import Loader from "../../../components/Loader";
import { formatNumber } from "../../../utils/helper";
import { createClient } from "../../../utils/supbase/client";
import Pagination from "../../../components/pagination";
import { useAddItemtoCartMutation } from "../../../redux/services/cart";
import { useAddItemtoWishlistMutation } from "../../../redux/services/wishlist";
import { useSelector } from "react-redux";
import NoItemsFound from "../../../components/noItemsFound";
import { toast } from "react-toastify";

export default function ShopGrid() {
  const location = useLocation();
  const supabase = createClient();
  const params = useParams();
  const { payload } = location.state || {};
  const [categoryId, setCategoryId] = useState(null);
  const user = useSelector((state) => state.auth.user);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalItems, setTotalItems] = useState();
  const pageLimit = 10;
  const offset = (pageNumber - 1) * pageLimit;
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState("name-asc");

  useEffect(() => {
    if (params.categoryId) {
      setCategoryId(params.categoryId);
    }
  }, [params]);

  const {
    data: products,
    error,
    isFetching: isLoading,
  } = useGetProductsQuery({
    filter: { category_id: categoryId },
    offset: offset,
    pageLimit: pageLimit,
  }, { skip: !categoryId });
  const [addProductToCart, { data }] = useAddItemtoCartMutation();
  const [addToWishlist] = useAddItemtoWishlistMutation();
  const [sortedProducts, setSortedProducts] = useState([]);

  async function getProductCountByCategory(categoryId) {
    const { data, error } = await supabase.rpc("products_count_by_categories", {
      cat_id: categoryId,
    });

    if (error) {
      console.error("Error calling RPC function:", error);
    } else {
      console.log("Total product count for category", categoryId, ":", data);
      setTotalItems(data);
    }
  }

  useEffect(() => {
    if (products?.length) {
      getProductCountByCategory(categoryId);
      let sorted = [...products];
      switch (sortBy) {
        case "name-asc":
          sorted.sort((a, b) => a.title.localeCompare(b.title));
          break;
        case "name-desc":
          sorted.sort((a, b) => b.title.localeCompare(a.title));
          break;
        case "price-asc":
          sorted.sort((a, b) => (a.sale_price || a.price) - (b.sale_price || b.price));
          break;
        case "price-desc":
          sorted.sort((a, b) => (b.sale_price || b.price) - (a.sale_price || a.price));
          break;
        default:
          break;
      }
      setSortedProducts(sorted);
    }
  }, [products, sortBy]);

  const handleAddToCart = (details) => {
    addProductToCart({
      quantity: 1,
      product_id: details.id,
      user_id: user.id,
    });
  };

  const handleAddToWishlist = async (details) => {
    try {
      await addToWishlist({
        product_id: details.id,
        user_id: user.id
      }).unwrap();
      toast.success("Added to wishlist!");
    } catch (error) {
      toast.error("Failed to add to wishlist");
    }
  };

  return (
    <>
      <Navbar navClass="defaultscroll is-sticky" />
      <section className="relative table w-full py-20 lg:py-24 md:pt-28 bg-gray-50 dark:bg-slate-800">
        <div className="container relative">
          <div className="grid grid-cols-1 mt-14">
            <h3 className="text-3xl leading-normal font-semibold">
              {params.categoryName ?? "Category"}
            </h3>
          </div>

          <div className="relative mt-3">
            <ul className="tracking-[0.5px] mb-0 inline-block">
              <li className="inline-block uppercase text-[13px] font-bold duration-500 ease-in-out hover:text-orange-500">
                <Link to="/">TAS e store</Link>
              </li>
              <li className="inline-block text-base text-slate-950 dark:text-white mx-0.5 ltr:rotate-0 rtl:rotate-180">
                <i className="mdi mdi-chevron-right"></i>
              </li>
              <li
                className="inline-block uppercase text-[13px] font-bold text-orange-500"
                aria-current="page"
              >
                {params.categoryName ?? "Category"}
              </li>
            </ul>
          </div>
        </div>
      </section>
      <section className="relative md:py-24 py-16">
        <div className="container relative">
          <div className="md:flex justify-between items-center mb-6">
            <span className="font-semibold">
              Showing 1-10 of {totalItems} items
            </span>

            <div className="md:flex items-center">
              <label className="font-semibold md:me-2">Sort by:</label>
              <select 
                className="form-select form-input md:w-40 w-full md:mt-0 mt-1 py-2 px-3 h-10 bg-transparent dark:bg-slate-900 dark:text-slate-200 rounded outline-none border border-gray-100 dark:border-gray-800 focus:ring-0"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="name-asc">Alfa A-Z</option>
                <option value="name-desc">Alfa Z-A</option>
                <option value="price-asc">Price Low-High</option>
                <option value="price-desc">Price High-Low</option>
              </select>
            </div>
          </div>
          {isLoading ? (
            <div className="flex justify-center items-center w-full">
              <Loader />
            </div>
          ) : sortedProducts?.length === 0 ? (
            <div className="flex justify-center items-center w-full">
              <NoItemsFound />
            </div>
          ) : (
            <div className="grid lg:grid-cols-4 md:grid-cols-3 sm:grid-cols-2 grid-cols-1 gap-6 justify-items-center">
              {sortedProducts?.map((item, index) => {
                return (
                  <button
                    className="group"
                    key={index}
                    onClick={() => navigate(`/product-detail-three/${item.id}`)}
                  >
                    <div className="relative overflow-hidden shadow dark:shadow-gray-800 group-hover:shadow-lg group-hover:dark:shadow-gray-800 rounded-md duration-500">
                      <img
                        src={
                          item?.image_urls?.length ? item.image_urls[0] : null
                        }
                        className="group-hover:scale-110 duration-500"
                        alt=""
                      />

                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddToCart(item);
                        }}
                        className="absolute -bottom-20 group-hover:bottom-3 start-3 end-3 duration-500"
                      >
                        <Link
                          to="/shop-cart"
                          className="py-2 px-5 inline-block font-semibold tracking-wide align-middle duration-500 text-base text-center bg-slate-900 text-white w-full rounded-md"
                        >
                          Add to Cart
                        </Link>
                      </div>

                      <ul className="list-none absolute top-[10px] end-4 opacity-0 group-hover:opacity-100 duration-500 space-y-1">
                        <li>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddToWishlist(item);
                            }}
                            className="size-10 inline-flex items-center justify-center tracking-wide align-middle duration-500 text-center rounded-full bg-white text-slate-900 hover:bg-slate-900 hover:text-white shadow"
                          >
                            <FiHeart className="size-4"></FiHeart>
                          </button>
                        </li>
                        <li className="mt-1 ms-0">
                          <Link
                            to={`/product-detail-three/${item.id}`}
                            className="size-10 inline-flex items-center justify-center tracking-wide align-middle duration-500 text-center rounded-full bg-white text-slate-900 hover:bg-slate-900 hover:text-white shadow"
                          >
                            <FiEye className="size-4"></FiEye>
                          </Link>
                        </li>
                        {/* <li className="mt-1 ms-0"><Link to="#" className="size-10 inline-flex items-center justify-center tracking-wide align-middle duration-500 text-center rounded-full bg-white text-slate-900 hover:bg-slate-900 hover:text-white shadow"><FiBookmark className="size-4"></FiBookmark></Link></li> */}
                      </ul>

                      <ul className="list-none absolute top-[10px] start-4">
                        {item.offer === true && (
                          <li>
                            <Link
                              to="#"
                              className="bg-orange-600 text-white text-[10px] font-bold px-2.5 py-0.5 rounded h-5"
                            >
                              {item.tag}
                            </Link>
                          </li>
                        )}
                        {item.tag === "New" && (
                          <li>
                            <Link
                              to="#"
                              className="bg-red-600 text-white text-[10px] font-bold px-2.5 py-0.5 rounded h-5"
                            >
                              {item.tag}
                            </Link>
                          </li>
                        )}
                        {item.tag === "Featured" && (
                          <li>
                            <Link
                              to="#"
                              className="bg-emerald-600 text-white text-[10px] font-bold px-2.5 py-0.5 rounded h-5"
                            >
                              {item.tag}
                            </Link>
                          </li>
                        )}
                      </ul>
                    </div>

                    <div className="mt-4 text-left">
                      <Link
                        to={`/product-detail-three/${item.id}`}
                        className="hover:text-orange-500 text-lg font-medium"
                      >
                        {item.title}
                      </Link>
                      <div className="flex justify-between items-center mt-1">
                        <p>
                          {item?.sale_price &&
                            `$${formatNumber(item.sale_price)}`}
                          <span
                            className={`${
                              item.sale_price &&
                              "line-through text-slate-400 ms-1"
                            } `}
                          >
                            {item.price ? `$${formatNumber(item.price)}` : null}
                          </span>
                        </p>
                        {/* Ratings <ul className="font-medium text-amber-400 list-none">
                                            <li className="inline"><i className="mdi mdi-star"></i></li>
                                            <li className="inline"><i className="mdi mdi-star"></i></li>
                                            <li className="inline"><i className="mdi mdi-star"></i></li>
                                            <li className="inline"><i className="mdi mdi-star"></i></li>
                                            <li className="inline"><i className="mdi mdi-star"></i></li>
                                        </ul> */}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          <Pagination
            currentPage={pageNumber}
            totalItems={totalItems}
            itemsPerPage={pageLimit}
            onPageChange={(page) => setPageNumber(page)}
          />
        </div>
      </section>
      <Footer />
      {/* <Switcher/> */}
      <ScrollToTop />
    </>
  );
}
